import React, { useState } from "react";
import { Field, Form } from "react-final-form";
import { Button } from "components/Common/Button";
import Row from "antd/lib/row";
import Col from "antd/lib/col";
import InputField from "components/UI/Form/InputField";
import Label from "components/UI/Form/label";
import {
	verifyPasswordNew,
	validateEmail,
	validatePhoneNumber,
	validateRequiredEncodedLength,
} from "helpers/Form/commonFormValidations";
import { useNavigate, useParams } from "react-router-dom";
import {
	useGetDomainGymQuery,
	useSignUpConsumerAtGymMutation,
	useSendOtpToPhoneNumberMutation,
	useVerifyOtpAndChangePhoneNumberMutation,
} from "store/api/api";
import { Paragraph9 } from "themes/default/_typography";
import { notification } from "antd";
import { useSetRecoilState } from "recoil";
import { userAtom } from "globalAtoms";
import { getUser } from "useInit";
import OTP from "./OTP";
import { USER_TYPES } from "constants/ui";
import { LoadingMask } from "components/Common/LoadingMask";

function SignUpForm({ preview, match }) {
	const setUser = useSetRecoilState(userAtom);
	const navigate = useNavigate();
	const [verifyPhone, setVerifyPhone] = useState();
	const [phoneNumber, setPhoneNumber] = useState();
	const { gym } = useParams();
	const { data: domainGymData } = useGetDomainGymQuery(gym);
	const [sendOtpToPhoneNumberTrigger, changePhone] = useSendOtpToPhoneNumberMutation();
	const [signUpConsumerAtGymTrigger, signUpConsumerAtGymResponse] = useSignUpConsumerAtGymMutation();
	const [verifyChangeNumber, verifyOtpAndChangePhoneNumber] = useVerifyOtpAndChangePhoneNumberMutation();
	const [loading, setLoading] = useState(false);

	const sendOtpToPhoneNumber = async ({ token, number }) => {
		const payload = {
			new_phone_number: number,
			token,
		};
		try {
			await sendOtpToPhoneNumberTrigger(payload).unwrap();
		} catch (error) {
			notification.error({
				message: error?.data?.message || "Error sending OTP. Please try again later.",
			});
		}
	};

	const logUserIn = async () => {
		setLoading(true);
		localStorage.setItem("creator_jwt_token", signUpConsumerAtGymResponse?.data?.auth_token);
		await getUser(setUser);
		navigate(`/${domainGymData?.subdomain}/dashboard/`);
		setLoading(false);
	};
	async function changePhoneNumber(values) {
		const newPhoneNumber = values.newPhoneNumber
			? `+${values.newPhoneNumber?.countryCode}${values.newPhoneNumber?.areaCode}${values.newPhoneNumber?.phoneNumber}`
			: phoneNumber;
		try {
			const payload = {
				phone_number: newPhoneNumber,
				code: values.otp,
				token: signUpConsumerAtGymResponse?.data?.auth_token,
			};

			await verifyChangeNumber(payload).unwrap();
			await logUserIn();
		} catch (error) {
			notification.error({
				message: error?.data?.message || "OTP is invalid. Please try again.",
			});
		}
	}

	const handleFormSubmit = async (formObj) => {
		try {
			if (preview) return;
			const newPhoneNumber = `+${formObj.phoneNumber?.countryCode}${formObj.phoneNumber?.areaCode}${formObj.phoneNumber?.phoneNumber}`;
			const json = {
				email_address: formObj.email,
				first_name: formObj.firstName,
				last_name: formObj.lastName,
				password: formObj.password,
				phone_number: newPhoneNumber,
				user_type: USER_TYPES.CONSUMER,
				registration_path: "web",
			};

			const { auth_token } = await signUpConsumerAtGymTrigger({ ...json, gym_id: domainGymData?.id }).unwrap();
			setPhoneNumber(newPhoneNumber);
			notification.success({
				message: "Successfully signed up!",
			});
			await sendOtpToPhoneNumber({
				token: auth_token,
				number: newPhoneNumber,
			});
			setVerifyPhone(true);
		} catch (error) {
			if (error.status === 409) {
				notification.error({
					message: "Email or phone are already in use. If you have an account, please log in.",
				});
			} else {
				notification.error({
					message: "Error signing up. Please try again later.",
				});
			}
		}
	};

	if (verifyOtpAndChangePhoneNumber.isLoading || loading) {
		return <LoadingMask />;
	}
	return (
		<>
			{verifyPhone ? (
				<OTP
					sendOtpToPhoneNumberTrigger={sendOtpToPhoneNumberTrigger}
					token={signUpConsumerAtGymResponse?.data?.auth_token}
					user={signUpConsumerAtGymResponse?.data?.user}
					phoneNumber={signUpConsumerAtGymResponse?.data?.user?.phone_number}
					changePhoneNumberButtonProps={{
						loading: changePhone.isLoading,
					}}
					resendOtpProps={{
						loading: changePhone.isLoading,
						onClick: async (e) => {
							e.preventDefault();
							logUserIn();
						},
						children: "Skip",
					}}
					continueButtonProps={{
						onClick: changePhoneNumber,
						loading: verifyOtpAndChangePhoneNumber.isLoading,
						disabled: verifyOtpAndChangePhoneNumber.isLoading,
					}}
				/>
			) : (
				<div className="sb-signup-form">
					<Form onSubmit={handleFormSubmit}>
						{({ handleSubmit }) => {
							return (
								<form id="signup" onSubmit={handleSubmit}>
									{preview && <Label>This creator is still setting up their gym, check back soon!</Label>}
									{!preview && (
										<div clasName="sb-signup-form-container">
											<Row gutter={[8, 16]}>
												<Col span={24}>
													<Label>Phone Number</Label>
													<Field name="phoneNumber" validate={validatePhoneNumber}>
														{({ input, meta }) => (
															<InputField
																data-id="txtPhone"
																disabled={preview}
																type="phone"
																input={input}
																meta={meta}
																className="phone-input"
																placeholder="Phone Number"
																size="small"
															/>
														)}
													</Field>
												</Col>
												<Col span={12}>
													<Label>First Name</Label>
													<Field
														component="input"
														name="firstName"
														validate={(firstName) => validateRequiredEncodedLength(firstName, 40)}
													>
														{({ input, meta }) => (
															<InputField
																data-id="txtFirstName"
																disabled={preview}
																type="text"
																input={input}
																meta={meta}
																placeholder={"First Name"}
																size="small"
															/>
														)}
													</Field>
												</Col>
												<Col span={12}>
													<Label>Last Name</Label>
													<Field
														component="input"
														name="lastName"
														validate={(lastName) => validateRequiredEncodedLength(lastName, 40)}
													>
														{({ input, meta }) => (
															<InputField
																data-id="txtLastName"
																disabled={preview}
																type="text"
																input={input}
																meta={meta}
																placeholder={"Last Name"}
																size="small"
															/>
														)}
													</Field>
												</Col>
												<Col span={24}>
													<Label>Email</Label>
													<Field component="input" name="email" validate={validateEmail}>
														{({ input, meta }) => (
															<InputField
																data-id="txtEmail"
																disabled={preview}
																type="text"
																input={input}
																meta={meta}
																placeholder={"Email"}
																size="small"
															/>
														)}
													</Field>
												</Col>
												<Col span={24}>
													<Label>Password</Label>
													<Field component="input" name="password" validate={verifyPasswordNew}>
														{({ input, meta }) => (
															<InputField
																data-id="txtPassword"
																disabled={preview}
																type="password"
																input={input}
																meta={meta}
																placeholder={"Password"}
															/>
														)}
													</Field>
												</Col>
												<Col span={24}>
													<Button
														data-id="btnSignUp"
														className="primary-filled--branding-primary-color"
														uppercase
														disabled={signUpConsumerAtGymResponse.isLoading}
														loading={signUpConsumerAtGymResponse.isLoading}
														type="submit"
														style={{
															backgroundColor: domainGymData?.brand_color1 !== "" ? domainGymData?.brand_color1 : null,
															padding: 0,
														}}
													>
														<Paragraph9>Sign Up</Paragraph9>
													</Button>
												</Col>
											</Row>
										</div>
									)}
								</form>
							);
						}}
					</Form>
				</div>
			)}
		</>
	);
}

export default SignUpForm;
